import {
  GiftDeliveryForAdmin,
  GiftDeliveryOrderInfo,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { GiftItemForStore } from '@shared/api/giftItem/giftItem.interface';
import { fileBucketUrl } from '@shared/utils/fileUtils';

export const formatDateTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  return `${y}.${m}.${day} ${h}시`;
};

export const formatDate = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  const y = String(d.getFullYear()).slice(2);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

// ===== 한국 시각(KST) 기준 표기/입력 유틸 =====
// 모든 시각 표기/입력은 한국 시각(KST) 기준. (브라우저가 미국 등 타 타임존이어도 동일)
const KST = 'Asia/Seoul';

// Date -> 한국 시각(KST)의 연/월/일/시/분 구성요소 (문자열, zero-padded)
const kstParts = (date: Date) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: KST,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
  };
};

// datetime-local input 기본값: 현재 한국 시각 (YYYY-MM-DDTHH:mm)
export const nowForInput = () => {
  const p = kstParts(new Date());
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};

// 기존 ISO 시각을 datetime-local 입력값(한국 시각 벽시계)으로 변환
export const isoToKstInput = (iso: Date | string) => {
  const p = kstParts(new Date(iso));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};

// 연도 없이 월.일 시:분 + 상대일(오늘/어제/n일 전), KST 기준 (배송요청·완료일 공용)
export const formatRelativeDateTime = (date: Date | string | undefined) => {
  if (!date) return null;
  const p = kstParts(new Date(date));
  const now = kstParts(new Date());

  // 달력(KST) 기준 일수 차이 (시:분 무시)
  const startOf = (q: ReturnType<typeof kstParts>) =>
    Date.UTC(Number(q.year), Number(q.month) - 1, Number(q.day));
  const diffDays = Math.round((startOf(now) - startOf(p)) / 86400000);
  let relative: string;
  if (diffDays <= 0) relative = '오늘';
  else if (diffDays === 1) relative = '어제';
  else relative = `${diffDays}일 전`;

  return {
    date: `${Number(p.month)}.${Number(p.day)}`,
    time: `${p.hour}:${p.minute}`,
    relative,
  };
};

// 발송/완료 시각: 연도 없이 월.일 시:분 (KST 기준, 배송요청 표기와 동일한 느낌)
export const formatShortDateTime = (date: Date | string | undefined) => {
  if (!date) return null;
  const p = kstParts(new Date(date));
  return `${Number(p.month)}.${Number(p.day)} ${p.hour}:${p.minute}`;
};

export const formatPhone = (phone: string | undefined) => {
  if (!phone) return '-';
  if (phone.length === 11)
    return `${phone.slice(0, 3)}-${phone.slice(3, 7)}-${phone.slice(7)}`;
  if (phone.length === 10)
    return `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`;
  return phone;
};

export const getItemName = (delivery: GiftDeliveryForAdmin): string => {
  if (typeof delivery.giftItem === 'object') {
    const item = delivery.giftItem as GiftItemForStore;
    return item.name || (item.giftItemInfo as any)?.name || '-';
  }
  return '-';
};

const sumPriceDelta = (options?: { priceDelta?: number }[]) =>
  (options ?? []).reduce((sum, o) => sum + (o.priceDelta || 0), 0);

const optionKey = (options?: { optionName?: string; choiceName?: string }[]) =>
  (options ?? []).map((o) => `${o.optionName}:${o.choiceName}`).join('|');

/**
 * 배송 건 구매금액 = (판매가 + 옵션추가금 + 소량추가금) × 수량.
 * 주문 라인의 구매 시점 동결값(giftItemEmbed) 기준. 주문 라인이 응답에 없으면(구서버·dashboard 미반영)
 * 현재 판매가(giftItem.priceRetail)로 폴백 — 이때 소량추가금은 알 수 없어 빠진다.
 * 권역 배송비(주문 단위)는 포함하지 않는다.
 */
export const getDeliveryPurchaseAmount = (
  delivery: GiftDeliveryForAdmin,
): number | null => {
  const optionDelta = sumPriceDelta(delivery.selectedOptions);
  const qty = delivery.quantity || 0;
  const itemId =
    typeof delivery.giftItem === 'object' ? delivery.giftItem._id : delivery.giftItem;

  const lines =
    typeof delivery.giftOrder === 'object' ? delivery.giftOrder.orderDetails : undefined;
  const sameItem = (lines ?? []).filter((l) => String(l.giftItem) === String(itemId));
  // 같은 상품이 옵션만 달리해 여러 라인일 수 있어 옵션까지 맞는 라인 우선
  const line =
    sameItem.find(
      (l) => optionKey(l.selectedOptions) === optionKey(delivery.selectedOptions),
    ) ?? sameItem[0];
  const embed = line?.giftItemEmbed;
  if (embed?.priceRetail != null) {
    return (embed.priceRetail + optionDelta + (embed.unitSurcharge || 0)) * qty;
  }

  if (typeof delivery.giftItem === 'object' && delivery.giftItem.priceRetail != null) {
    return (delivery.giftItem.priceRetail + optionDelta) * qty;
  }
  return null;
};

/**
 * 공급사(giftBrand) 콘솔용 상품명.
 * 상조별 판매이름 오버라이드(giftItem.name)는 노출하지 않고,
 * 업체와 계약된 원상품명(giftItemInfo.name)만 표시한다.
 */
export const getBrandItemName = (delivery: GiftDeliveryForAdmin): string => {
  if (typeof delivery.giftItem === 'object') {
    const info = (delivery.giftItem as GiftItemForStore).giftItemInfo;
    if (info && typeof info === 'object' && info.name) return info.name;
  }
  return '-';
};

export const getImageUrl = (delivery: GiftDeliveryForAdmin): string => {
  if (typeof delivery.giftItem === 'object') {
    const item = delivery.giftItem as GiftItemForStore;
    const img = item.image;
    if (img && typeof img === 'object' && '_id' in img) {
      return fileBucketUrl(img._id, 'w200');
    }
  }
  return '';
};

export const getOrdererInfo = (delivery: GiftDeliveryForAdmin) => {
  if (typeof delivery.giftOrder !== 'object') {
    return { name: '-', phone: '-', purchasedAt: undefined };
  }
  const order = delivery.giftOrder as GiftDeliveryOrderInfo;
  return {
    name: order.bugo?.member?.fullName || order.receiverName || '-',
    phone: order.bugo?.member?.phoneNumber || order.receiverPhoneNumber || '-',
    purchasedAt: order.paymentDetail?.purchasedAt,
  };
};
